import React, { useState, useEffect } from "react";
import { 
  Box, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TextField, List, ListItem, ListItemText, ListItemIcon, Avatar, CircularProgress, Alert
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import VideocamIcon from '@mui/icons-material/Videocam';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";

const CamerasInformation = () => {
  const [cameraFilter, setCameraFilter] = useState("");
  const [cameras, setCameras] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useRecoilValue(authState);

  // Real-Time Activity Monitor — polling كل 10 ثواني
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoadingSessions(prev => sessions.length === 0 ? true : prev);
        const { data } = await axios.get(
          `${baseURL}shoplifting/dashboard/real-time-activity`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessions(Array.isArray(data) ? data : (data.data || []));
      } catch (err) {
        console.error("Error fetching sessions:", err);
      } finally {
        setLoadingSessions(false);
      }
    };

    if (token) {
      fetchSessions();
      const interval = setInterval(fetchSessions, 10000);
      return () => clearInterval(interval);
    }
  }, [token]);

  // Camera Health — debounced fetch عند تغيير الفلتر
  useEffect(() => {
    const fetchCameras = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(
          `${baseURL}shoplifting/dashboard/camera-health`,
          {
            params: { camera_name: cameraFilter },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setCameras(Array.isArray(data) ? data : (data.data || []));
      } catch (err) {
        console.error("Error fetching cameras:", err);
        setError("Failed to load camera data.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      const delayDebounceFn = setTimeout(() => {
        fetchCameras();
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [token, cameraFilter]);

  // فلتر بناءً على detection_confidence (الـ field الصح من الـ API)
  const activeThreats = sessions
    .filter(s => parseFloat(s.detection_confidence) > 0.8)
    .sort((a, b) => b.detection_confidence - a.detection_confidence);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>

        {/* Real-time Store Activity Monitor */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <WarningAmberIcon color="error" />
              <Typography variant="h6" fontWeight="bold">Real-Time Activity Monitor</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Live sessions displaying high-risk behavioral patterns.
            </Typography>

            {loadingSessions && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!loadingSessions && (
              <List>
                {activeThreats.slice(0, 5).map((session, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'error.main',
                      color: 'error.contrastText',
                      opacity: 0.9,
                      '&:hover': { opacity: 1, transform: 'translateX(5px)', transition: '0.2s' }
                    }}
                  >
                    <ListItemIcon>
                      <VideocamIcon sx={{ color: 'white' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        `State: ${session.behavior_state} — ${session.behavior_category} ` +
                        `(Conf: ${(session.detection_confidence * 100).toFixed(0)}%)`
                      }
                      secondary={
                        <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          {session.building} &gt; {session.floor_level} &gt; {session.zone} &gt; {session.camera_name}
                        </Typography>
                      }
                    />
                    <Chip
                      size="small"
                      label={new Date(session.last_seen).toLocaleTimeString()}
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', flexShrink: 0 }}
                    />
                  </ListItem>
                ))}
                {activeThreats.length === 0 && (
                  <Typography>No high-risk sessions at this moment.</Typography>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Global Traffic & Insight Stats */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>Global Store Traffic & KPIs</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                  <Typography variant="body2" color="text.secondary">Avg Session Duration</Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    60
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography variant="body2" paragraph>
                    <strong>Peak Store Traffic:</strong> Saturdays between 14:00 and 18:00
                  </Typography>
                  <Typography variant="body2">
                    <strong>High Risk Window:</strong> Fridays between 16:00 and 20:00
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Camera Health & Coverage */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Camera Health & Coverage Audit</Typography>
              <TextField
                size="small"
                placeholder="Filter by Camera or Zone..."
                InputProps={{ startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} /> }}
                value={cameraFilter}
                onChange={(e) => setCameraFilter(e.target.value)}
              />
            </Box>

            <TableContainer sx={{ maxHeight: 400 }}>
              {loading && (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              )}
              {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

              {!loading && !error && (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Camera Name</strong></TableCell>
                      <TableCell><strong>Zone</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Observations (Uptime Check)</strong></TableCell>
                      <TableCell><strong>Installed Date</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cameras.map((cam) => (
                      <TableRow key={cam.stream_id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: cam.status === 'active' ? 'success.main' : 'error.main' }}>
                              <VideocamIcon fontSize="small" />
                            </Avatar>
                            {cam.camera_name}
                          </Box>
                        </TableCell>
                        <TableCell>{cam.zone}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={cam.status ? cam.status.toUpperCase() : 'UNKNOWN'}
                            color={cam.status === 'active' ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            color={cam.observation_count < 200 ? 'error' : 'inherit'}
                            fontWeight={cam.observation_count < 200 ? 'bold' : 'normal'}
                          >
                            {cam.observation_count}
                          </Typography>
                        </TableCell>
                        <TableCell>{new Date(cam.installed_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                    {cameras.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No cameras found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default CamerasInformation;